/* eslint-disable max-lines */
import { Image, LinkButton, Responsive, TDeviceType } from '@cian/ui-kit';
import { IconPlaceholderImage16, IconCommunicationPhone16 } from '@cian/ui-kit-design-tokens/icons';
import classNames from 'classnames';
import * as React from 'react';

import * as styles from './Card.css';
import { CardContent } from './internal/components/CardContent';
import { trackCardClick } from './tracking';
// eslint-disable-next-line import/no-restricted-paths
import { callbackIfIdle } from '../../../browser/utils/callbackIfIdle';
import { BX_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT } from '../../constants/experiments';
import { NBSP } from '../../constants/text';
import { ChatButtonContainer } from '../../containers/ChatButtonContainer';
import { ElectronicTradingButtonContainer } from '../../containers/ElectronicTradesButtonContainer';
import { useIsDailyrentOnlineBookingEnabled } from '../../hooks/useIsDailyrentOnlineBookingEnabled';
import { ECommunicationType } from '../../repositories/favorites/entities/favorite_entity_for_desktop/FavoriteEntityForDesktopSchema';
import { IPageState } from '../../types/page';
import { IFavoriteEntity } from '../../types/results';
import { useApplicationContext } from '../../utils/applicationContext';
import { showDailyrentActionButton } from '../../utils/showDailyrentActionButton';
import { BookButton } from '../BookButton';
import { BookedOfferDescription } from '../BookedOfferDescription';
import { CardActionsLayout } from '../CardActionsLayout';
import { CardShields } from '../CardShields';
import { Favorites } from '../Favorites';
import { LinkArea } from '../LinkArea';
import { OnlineBookingBookButton } from '../OnlineBookingBookButton';
import { trackCall, trackPhoneButtonClick } from '../Phone/tracking';
import { PhoneButton } from '../PhoneButton';
import { PublicationRemovalNotice } from '../PublicationRemovalNotice';
import { TabletCardControls } from '../TabletCardControls';
import { experimentToggle } from '../experimentToggle';

export interface ICardProps {
  favoriteEntity: IFavoriteEntity;
  page: IPageState;
  total: number;
  position: number;

  removeFavorite(id: number): void;
}

export const Card: React.FC<ICardProps> = ({ total, favoriteEntity, page, position, removeFavorite }) => {
  const trackMediaClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_photo' });
  }, [favoriteEntity, page.deviceType, position, total]);
  const trackTitleClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_header' });
  }, [favoriteEntity, page.deviceType, position, total]);
  const trackJKButtonClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_button' });
  }, [favoriteEntity, page.deviceType, position, total]);
  const trackShowNumber = React.useCallback(() => {
    trackPhoneButtonClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType });
  }, [favoriteEntity, page, position, total]);
  const trackShowNumberMobile = React.useCallback(() => {
    trackPhoneButtonClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType });
    callbackIfIdle(() => trackCall({ entity: favoriteEntity, position, total, deviceType: page.deviceType }), 10000);
  }, [favoriteEntity, page, position, total]);
  const handleRemove = React.useCallback(() => {
    removeFavorite(favoriteEntity.entityId);
  }, [favoriteEntity.entityId, removeFavorite]);

  const {
    agentInfo,
    title,
    fullUrl,
    externalOfferUrl,
    photoPreviewUrl,
    photoFullUrl,
    bargainTerms,
    jkInfo,
    geo,
    description,
    phone,
    commercialInfo,
    status,
    deleteDate,
    commercialOwnership,
    communicationType = ECommunicationType.Phone,
    titleForMobileSite,
    isCianPartner,
    tradingLink,
    dailyrent,
    labels,
    isBookedFromDeveloper,
  } = favoriteEntity;
  const href = isCianPartner ? (externalOfferUrl as string) : fullUrl;
  const target = isCianPartner ? '_blank' : undefined;
  const actionType = dailyrent?.actionType;

  const hasImage = photoPreviewUrl || photoFullUrl;

  const isDailyrentOnlineBookingEnabled = useIsDailyrentOnlineBookingEnabled();

  const { config } = useApplicationContext();
  /**
   * @todo Удалить переменную когда будет удален фича-тогл
   * @description Данный функционал появился в задачах CD-192026 и CD-194164, будет удалён в задаче CD-192195
   */
  const electronicTradingEnabled = config.get<Boolean>('FavoritesMobile.ElectronicTrading.Enabled');
  const shouldShowShieldsUnderTitle = electronicTradingEnabled;
  const isElectronicTradingAvailable = electronicTradingEnabled && !!tradingLink;

  const isBookedFromDeveloperEnabled = config.get<Boolean>('Newbuilding.BookedFromDeveloperDesktop.Enabled');
  const isBooked = Boolean(isBookedFromDeveloperEnabled && isBookedFromDeveloper);

  /**
   * @todo Удалить переменную когда будет удален фича-тогл redesignFavorites.Enabled
   */
  const isSavedSearchFavoritesMergeExperimentActive = config.get<boolean>('redesignFavorites.Enabled') || false;

  const { showOtaCheckDatesButton, showPartnerCheckDatesButton, hideActionButton } = showDailyrentActionButton(
    isDailyrentOnlineBookingEnabled,
    actionType,
  );

  const actionButton = (() => {
    if (isElectronicTradingAvailable) {
      return <ElectronicTradingButtonContainer favoriteEntity={favoriteEntity} deviceType={'phone' as TDeviceType} />;
    }

    if (communicationType === ECommunicationType.Forms) {
      return (
        <LinkButton href={`${fullUrl}?opendealrentform=true`} fullWidth>
          Назначить просмотр
        </LinkButton>
      );
    }

    if (showPartnerCheckDatesButton) {
      return (
        <BookButton
          phone={phone}
          favoriteEntity={favoriteEntity}
          page={page}
          total={total}
          size="M"
          position={position}
        />
      );
    }

    if (showOtaCheckDatesButton) {
      return (
        <OnlineBookingBookButton entity={favoriteEntity} href={href} offersQty={total} position={position} size="M" />
      );
    }

    if (hideActionButton) {
      return null;
    }

    return (
      <PhoneButton
        phone={phone}
        onClick={trackShowNumberMobile}
        text={isBooked ? `Уточнить у${NBSP}застройщика` : undefined}
        beforeIcon={isBooked ? <IconCommunicationPhone16 color="icon-on-bright-default" /> : undefined}
      />
    );
  })();

  /**
   * Временное решение вынести карточку в отдельную переменную, чтобы упростить код внутри experimentToggle
   */
  const Card = (
    <>
      <LinkArea className={styles['media']} href={href} target={target} onClick={trackMediaClick}>
        <div
          className={classNames(
            styles['gallery'],
            isSavedSearchFavoritesMergeExperimentActive && styles['rounted_gallery'],
          )}
          data-testid="card_image"
        >
          {hasImage ? (
            <Image
              src={photoPreviewUrl || photoFullUrl || ''}
              height={178}
              alt="Главное фото объявления"
              objectFit="cover"
              borderRadius={0}
            />
          ) : (
            <div className={styles['empty_photo']}>
              <IconPlaceholderImage16 color="gray20_100" />
            </div>
          )}
          {/*
           * @todo Удалить блок с шильдиками и класс shields_wrapper--legacy когда будет удален фича-тогл
           * @description Данный функционал появился в задаче CD-192026, будет удалён в задаче CD-192195
           */}
          {!shouldShowShieldsUnderTitle && (
            <div data-testid="card-shields" className={styles['shields_wrapper--legacy']}>
              <CardShields
                agentLists={agentInfo?.agentLists}
                isCommericalOwner={!!commercialOwnership?.isFromOwner}
                isMobile
              />
            </div>
          )}
        </div>
        <PublicationRemovalNotice status={status} deleteDate={deleteDate} />
      </LinkArea>
      <div className={styles['content']}>
        <LinkArea className={styles['content']} href={href} target={target} onClick={trackTitleClick}>
          {
            /**
             * @todo experimentToggle - временное решение, удалить после завершения эксперимента aud_mobile_bx585_saved_search_favorites_merge
             */
            experimentToggle(
              isSavedSearchFavoritesMergeExperimentActive,
              BX_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT,
              <div className={styles['header']}>
                <CardContent
                  title={title}
                  bargainTerms={bargainTerms}
                  geo={geo}
                  labels={labels}
                  shouldShowShieldsUnderTitle={!!shouldShowShieldsUnderTitle}
                  handleRemoveFavorites={handleRemove}
                  trackTitleClick={trackTitleClick}
                  isRedesignExperimentActive={isSavedSearchFavoritesMergeExperimentActive}
                  agentInfo={agentInfo}
                  commercialInfo={commercialInfo}
                  description={description}
                  jkInfo={jkInfo}
                  titleForMobileSite={titleForMobileSite}
                  commercialOwnership={commercialOwnership}
                  isBooked={isBooked}
                />
              </div>,
              <div>
                <CardContent
                  title={title}
                  bargainTerms={bargainTerms}
                  geo={geo}
                  labels={labels}
                  shouldShowShieldsUnderTitle={!!shouldShowShieldsUnderTitle}
                  handleRemoveFavorites={handleRemove}
                  trackTitleClick={trackTitleClick}
                  isRedesignExperimentActive={isSavedSearchFavoritesMergeExperimentActive}
                  agentInfo={agentInfo}
                  commercialInfo={commercialInfo}
                  description={description}
                  jkInfo={jkInfo}
                  titleForMobileSite={titleForMobileSite}
                  commercialOwnership={commercialOwnership}
                  isBooked={isBooked}
                />
              </div>,
            )
          }
        </LinkArea>
        <Responsive phone>
          {isBooked && <BookedOfferDescription />}
          <CardActionsLayout isRedesignExperimentActive={isSavedSearchFavoritesMergeExperimentActive}>
            {actionButton}
            <ChatButtonContainer
              offer={favoriteEntity}
              position={position}
              total={total}
              deviceType={page.deviceType}
            />
          </CardActionsLayout>
        </Responsive>
        <div className={styles['controls']}>
          <TabletCardControls
            favoriteEntity={favoriteEntity}
            communicationType={communicationType}
            phone={phone}
            jkInfo={jkInfo}
            fullUrl={fullUrl}
            trackPhoneClick={trackShowNumber}
            trackJKButtonClick={trackJKButtonClick}
            page={page}
            total={total}
            position={position}
          />
          {
            /**
             * @todo experimentToggle - временное решение, удалить после завершения эксперимента aud_mobile_bx585_saved_search_favorites_merge
             */
            experimentToggle(
              isSavedSearchFavoritesMergeExperimentActive,
              BX_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT,
              <div className={styles['favorite']}>
                <Favorites onClick={handleRemove} />
              </div>,
            )
          }
        </div>
      </div>
    </>
  );

  /**
   * @todo experimentToggle - временное решение, удалить после завершения эксперимента aud_mobile_bx585_saved_search_favorites_merge
   */
  return experimentToggle(
    isSavedSearchFavoritesMergeExperimentActive,
    BX_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT,
    <div className={styles['card']} data-testid="favorite-offer-card">
      {Card}
    </div>,
    <div className={styles['card--redesign']} data-testid="favorite-offer-card">
      {Card}
    </div>,
  );
};

Card.displayName = 'Card';
